import { pickGroupWeather } from '../GroupWeather';
import type {
  WorkOrderWeather,
  WorklogFeedItem,
  WorklogTimelineItem,
} from '../../types/worklog.types';

/**
 * 공사현황 피드 공용 모델 — 공사현황 전체 화면(WorklogStatusPage)과 나의 현장 작업공간 '공사현황' 탭(SiteWorkFeed)이 같이 쓴다.
 * 서버 통합 타임라인(§1.5) 항목 → 화면용 피드 항목 변환, 필터·정렬, 날짜 그룹 안 작업요청↔현장보고 페어링, 표기 유틸.
 */

/** 작성자 필터의 로그인 사장님(OWNER) pseudo-partner 식별자 — 실제 partnerId와 안 겹치는 음수(현장보고 목록과 동일). */
export const OWNER_AUTHOR_ID = -1;

/** 선정(작업) 모드 — 인디포토 저장만 남음(REQ-4-06, 고객공유는 진입점5로 이관). */
export type WorkMode = null | 'photo';

/**
 * 인디포토 지정(선정) 로컬 상태 — fieldReportId 별. 고객공유는 진입점5로 이관(REQ-4-06).
 */
export interface Designation {
  ipPhotos: Set<number>; // 인디포토 전송 대상 사진 index
}

// ── 날짜 유틸 ────────────────────────────────────────────────
export const pad2 = (n: number) => String(n).padStart(2, '0');
export const toDash = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const DOW = ['일', '월', '화', '수', '목', '금', '토'];
export function dateKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/** ISO → '2026.06.11 (목)' */
export function fmtDateLabel(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} (${DOW[d.getDay()]})`;
}
/** ISO → '2026.10.06 10:34'(24시간) — 카드 '작성일시' */
export function fmtReportDtm(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
/** 'YYYY-MM-DD' → '2026.10.06' — 카드 '작업일' */
export function fmtWorkDate(date: string) {
  return date.slice(0, 10).replace(/-/g, '.');
}
export function fmtTime(iso: string) {
  const d = new Date(iso);
  const h = d.getHours();
  const m = pad2(d.getMinutes());
  const ampm = h < 12 ? '오전' : '오후';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${ampm} ${h12}:${m}`;
}

// ── 문장 분리("외 N건" 펼침용, REQ-4-05) ──────────────────────
// WorkOrder.orderContent·FieldReport.reportContent 둘 다 단일 텍스트다. 줄바꿈 기준으로 문장을
// 분리해 "첫 문장 + 외 N건"으로 축약하고, "상세보기"로 펼친다(빈 줄 제거, 없으면 통짜 1문장).
export function splitSentences(text: string): string[] {
  const parts = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts : [text];
}

// ── 서버 타임라인 → 화면 피드 ─────────────────────────────────
/** 서버 통합 타임라인 항목을 카드 렌더·필터·그룹핑용 피드 항목으로 정규화한다. */
export function toFeedItems(items: WorklogTimelineItem[]): WorklogFeedItem[] {
  return items.map<WorklogFeedItem>((it) =>
    it.type === 'WORK_ORDER'
      ? { kind: 'wo', dtm: it.occurredAt, siteId: it.siteId, wo: it.workOrder! }
      : { kind: 'fr', dtm: it.occurredAt, siteId: it.siteId, fr: it.fieldReport! },
  );
}

/**
 * 피드 필터·정렬.
 * - siteFilter: 현장 필터(공사현황 전체 화면의 우측 패널 선택). 단일 현장 피드는 서버가 이미 걸러 오므로 생략.
 * - partnerIds: 작성자 필터 — WO는 수신자(recipients), FR은 작성자(writerId). 로그인 사용자(OWNER) 선택 시 OWNER 작성 현장보고 포함.
 * - 정렬: 날짜 단위로 최신순/과거순, 동일 날짜 내(작업요청·현장보고 혼합)는 정렬 방향과 무관하게 항상 과거순.
 */
export function filterSortFeed(
  feed: WorklogFeedItem[],
  opts: { siteFilter?: (siteId: number) => boolean; partnerIds: Set<number>; sortDesc: boolean },
): WorklogFeedItem[] {
  const { siteFilter, partnerIds, sortDesc } = opts;
  let list = siteFilter ? feed.filter((it) => siteFilter(it.siteId)) : [...feed];
  if (partnerIds.size > 0) {
    const includeOwner = partnerIds.has(OWNER_AUTHOR_ID);
    list = list.filter((it) =>
      it.kind === 'wo'
        ? it.wo.recipients.some((r) => partnerIds.has(r.partnerId))
        : partnerIds.has(it.fr.writerId) || (includeOwner && it.fr.reportSource === 'OWNER'),
    );
  }
  list.sort((a, b) => {
    const dateCmp = dateKey(a.dtm).localeCompare(dateKey(b.dtm));
    if (dateCmp !== 0) return sortDesc ? -dateCmp : dateCmp;
    return a.dtm.localeCompare(b.dtm);
  });
  return list;
}

/** 서버가 내려준 기존 선별 — fieldReportId → 인디포토 전송된 사진 index(REQ-4-04) */
export function buildServerShare(items: WorklogTimelineItem[]): Record<number, { ipPhotos: number[] }> {
  const map: Record<number, { ipPhotos: number[] }> = {};
  for (const it of items) {
    if (it.type !== 'FIELD_REPORT' || !it.fieldReport) continue;
    const fr = it.fieldReport;
    const ipPhotos: number[] = [];
    fr.photos.forEach((p, pi) => {
      if (p.indiPhotoSentYn === 'Y') ipPhotos.push(pi);
    });
    map[fr.fieldReportId] = { ipPhotos };
  }
  return map;
}

// ── 그룹핑: 날짜 → 통합 항목 ─────────────────────────────────
interface DateGroup {
  key: string;
  sample: string; // 대표 dtm(날짜 라벨용)
  items: WorklogFeedItem[];
}
function groupByDate(items: WorklogFeedItem[]): DateGroup[] {
  const map = new Map<string, DateGroup>();
  for (const it of items) {
    const dk = dateKey(it.dtm);
    let dg = map.get(dk);
    if (!dg) {
      dg = { key: dk, sample: it.dtm, items: [] };
      map.set(dk, dg);
    }
    dg.items.push(it);
  }
  return [...map.values()];
}

export type WoFeedItem = Extract<WorklogFeedItem, { kind: 'wo' }>;
export type FrFeedItem = Extract<WorklogFeedItem, { kind: 'fr' }>;

/** 페어 행 1건 — 작업요청(wo) 1건 + 매칭된 현장보고(frs) 0건 이상, 또는 매칭 안 된 현장보고 단독(wo=null). */
export interface PairRow {
  wo: WoFeedItem | null;
  frs: FrFeedItem[];
}
export interface PairDateGroup {
  key: string;
  sample: string;
  rows: PairRow[];
  woCount: number;
  frCount: number;
  /** 날짜 그룹 머리에 보일 날씨 — 그날 날씨가 있는 가장 이른 작업요청의 스냅샷. 없으면 null */
  weather: WorkOrderWeather | null;
}

/**
 * 날짜 그룹 안에서 작업요청↔현장보고를 매칭해 페어 행으로 재구성한다(REQ-4-01, 결정 4-1).
 * 매칭키는 FieldReport.workOrderId(FK, 기존 컬럼) — 실측 목업의 "이름 일치" 방식보다 결정적이라 이걸 쓴다.
 * 날짜 그룹은 **작업요청일 기준**이다 — 서버(타임라인)가 현장보고의 occurredAt을 연결된 작업요청의 발송일시로 내려주므로
 * 작업요청 10/2건에 10/6에 올린 보고도 10/2 그룹에서 같은 갈래로 묶인다(보고 자신의 제출 시각·작업일은 카드에 표시).
 * 매칭 안 된 현장보고(사장님 직접 작성 포함)는 wo=null 단독 행으로 — 좌측은 비워둔다(목업·DEV_RULES 그대로,
 * "사장님 직접 등록 안내 카드" 문구는 만들지 않기로 함).
 */
export function buildPairGroups(items: WorklogFeedItem[]): PairDateGroup[] {
  return groupByDate(items).map((dg) => {
    const woItems = dg.items.filter((it): it is WoFeedItem => it.kind === 'wo');
    const frItems = dg.items.filter((it): it is FrFeedItem => it.kind === 'fr');
    const matchedFrIds = new Set<number>();
    const rows: PairRow[] = woItems.map((woItem) => {
      const matches = frItems.filter((fr) => fr.fr.workOrderId === woItem.wo.workOrderId);
      matches.forEach((m) => matchedFrIds.add(m.fr.fieldReportId));
      return { wo: woItem, frs: matches };
    });
    const standalone: PairRow[] = frItems
      .filter((fr) => !matchedFrIds.has(fr.fr.fieldReportId))
      .map((fr) => ({ wo: null, frs: [fr] }));
    return {
      key: dg.key,
      sample: dg.sample,
      rows: [...rows, ...standalone],
      woCount: woItems.length,
      frCount: frItems.length,
      weather: pickGroupWeather(woItems, (it) => it.wo.weather, (it) => it.dtm),
    };
  });
}
